import CloseIcon from '@mui/icons-material/Close';
import {
  Box,
  DialogActions,
  DialogContent,
  DialogProps,
  DialogTitle,
  IconButton,
  Modal,
  Stack,
  SwipeableDrawer,
  SwipeableDrawerProps,
  styled,
  Typography,
} from '@mui/material';
import { grey } from '@mui/material/colors';
import { useMobile } from '../../hooks';

const Puller = styled(Box)(({ theme }) => ({
  width: 30,
  height: 6,
  backgroundColor: theme.palette.mode === 'light' ? grey[300] : grey[900],
  borderRadius: 3,
  position: 'absolute',
  top: 8,
  left: 'calc(50% - 15px)',
}));

interface SimpleDialogProps {
  title: string;
  open: boolean;
  children: React.ReactNode;
  actions?: React.ReactNode;
  width?: number;
  closeButton?: boolean;
  onClose: () => void;
  onOpen?: () => void;
  'data-testid'?: string;
}

export default function SimpleDialog(
  props: SimpleDialogProps & (DialogProps | SwipeableDrawerProps)
) {
  const isMobile = useMobile();

  const {
    onClose,
    onOpen,
    title,
    open,
    children,
    actions,
    width,
    closeButton,
    'data-testid': testId,
  } = props;

  if (isMobile) {
    return (
      <SwipeableDrawer
        open={open}
        onClose={onClose}
        onOpen={
          onOpen ||
          (() => {
            /* no-op */
          })
        }
        anchor="bottom"
        disableSwipeToOpen
        disableDiscovery
      >
        <Puller />
        <DialogTitle sx={{ px: 2 }}>{title}</DialogTitle>
        <DialogContent sx={{ px: 2 }}>{children}</DialogContent>
        <DialogActions>{actions}</DialogActions>
      </SwipeableDrawer>
    );
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      aria-labelledby="modal-modal-title"
      aria-describedby="modal-modal-description"
      data-testid={testId || null}
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <Box
        sx={{
          maxHeight: '80%',
          overflow: 'hidden',
          width: { width },
          bgcolor: 'background.menu',
          borderRadius: '5px',
          boxShadow: 24,
          p: 4,
          display: 'flex',
          flexDirection: 'column',
          outline: 'none',
        }}
      >
        <Stack
          direction="row"
          spacing={0}
          sx={{
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <Typography
            id="modal-modal-title"
            variant="h6"
            component="h2"
            color="text.primary"
          >
            {title}
          </Typography>
          {closeButton ? (
            <IconButton
              aria-label="close"
              onClick={onClose}
              sx={(theme) => ({
                color: theme.palette.grey[500],
              })}
            >
              <CloseIcon />
            </IconButton>
          ) : null}
        </Stack>
        <Box
          sx={{
            flex: 1,
            overflowY: 'auto',
            scrollbarWidth: 'thin',
          }}
        >
          {children}
        </Box>
        {actions && <DialogActions>{actions}</DialogActions>}
      </Box>
    </Modal>
  );
}

SimpleDialog.defaultProps = {
  actions: null,
  width: 400,
  closeButton: false,
  onOpen: () => {
    /* no-op */
  },
  'data-testid': null,
};
